/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import GalleryNavigation from 'src/components/core/slider/gallery-navigation';
import React, {
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  sliderRef: MutableRefObject<any>,
  total: number
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${units(3)};
`;

/**
 * `Scrollbar` styled component.
 *
 * The 40px tall element is the drag area; the visible track is 2px.
 */

const Scrollbar = styled.div`
  cursor: pointer;
  flex: 1 1 auto;
  height: ${units(5)};
  min-width: 0;
  position: relative;

  &::before {
    background-color: ${color('grey200')};
    content: '';
    height: 2px;
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
  }

  &.swiper-scrollbar-lock {
    display: none;
  }

  .slider-controls-drag {
    height: 100%;
    position: relative;

    &::before {
      background-color: ${color('black')};
      content: '';
      height: 2px;
      left: 0;
      position: absolute;
      right: 0;
      top: 50%;
    }
  }
`;

/**
 * `StyledGalleryNavigation` styled component.
 */

const StyledGalleryNavigation = styled(GalleryNavigation)`
  display: none;

  ${media.min('ms')`
    display: flex;
    flex-shrink: 0;
  `}

  button {
    align-items: center;
    color: ${color('black')} !important;
    display: flex;
    height: ${units(5)};
    justify-content: center;
    width: ${units(5)};
  }

  button:disabled {
    color: ${color('grey600')} !important;
  }
`;

/**
 * `SliderControls` component.
 *
 * Draggable scrollbar plus previous/next arrows (desktop only) for a `Slider`
 * rendered elsewhere, controlled through its `ref`.
 */

const SliderControls = (props: Props): ReactElement => {
  const { className, sliderRef, total } = props;
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState<number>(0);
  const handlePrevious = useCallback(() => {
    sliderRef.current?.swiper?.slidePrev();
  }, [sliderRef]);

  const handleNext = useCallback(() => {
    sliderRef.current?.swiper?.slideNext();
  }, [sliderRef]);

  // The scrollbar lives outside the swiper element, so it is attached by
  // reference (a class selector would be shared by every slider on the page).
  useEffect(() => {
    const swiper = sliderRef.current?.swiper;

    if (!swiper || !scrollbarRef.current) {
      return;
    }

    const handleSlideChange = () => setCurrent(swiper.activeIndex);

    if (swiper.scrollbar.el !== scrollbarRef.current) {
      swiper.params.scrollbar = {
        ...swiper.params.scrollbar,
        dragClass: 'slider-controls-drag',
        draggable: true,
        el: scrollbarRef.current,
        hide: false
      };

      swiper.scrollbar.init();
      swiper.scrollbar.updateSize();
      swiper.scrollbar.setTranslate();
    }

    swiper.on('slideChange', handleSlideChange);

    return () => {
      swiper.off('slideChange', handleSlideChange);
    };
  }, [sliderRef, total]);

  return (
    <Wrapper className={className}>
      <Scrollbar ref={scrollbarRef} />

      {total > 1 && (
        <StyledGalleryNavigation
          current={current}
          onGoToNext={handleNext}
          onGoToPrevious={handlePrevious}
          total={total}
        />
      )}
    </Wrapper>
  );
};

/**
 * Export `SliderControls` component.
 */

export default SliderControls;
